/**
 * src/walletConnectCapability.ts
 *
 * Turns a WalletConnect session refusal into an honest, specific explanation.
 *
 * WHY THIS EXISTS
 * ---------------
 * GeoTrust runs on testnet, so the wallets-kit WalletConnect module proposes:
 *
 *     requiredNamespaces.stellar = {
 *       chains:  ["stellar:testnet"],     // from allowedChains
 *       methods: ["stellar_signXDR"],
 *     }
 *
 * Under the WalletConnect v2 namespace spec a wallet must refuse the whole
 * proposal if it cannot serve everything in `requiredNamespaces`. That is the
 * correct behaviour and it is what keeps "GeoTrust is on testnet" a guarantee
 * rather than a hope - a pubnet-only wallet can never be talked into a session
 * we would then mistake for testnet.
 *
 * The cost is that a capability mismatch arrives looking like a connection
 * failure, and the refusal often carries the word "rejected", so it used to be
 * reported to the user as "Wallet connection was cancelled by user". That is
 * doubly wrong: the user cancelled nothing, and it hides the real reason.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * It does not decide *which* wallet lacks *what*. It reports only what the
 * wallet itself said during negotiation. When the wallet refuses without
 * distinguishing chain from method - which is the common case, and the one
 * LOBSTR has historically produced - the message says so instead of guessing.
 * A capability claim we cannot back with the wallet's own answer is not one we
 * make, and "this wallet's WalletConnect bridge" is never widened into "this
 * wallet", because the same wallet may well support testnet by other means.
 */

/** The chain GeoTrust requires. Mirrors `allowedChains` passed to the kit. */
export const REQUIRED_CHAIN = "stellar:testnet";
/** The method the kit puts in `requiredNamespaces`. */
export const REQUIRED_METHOD = "stellar_signXDR";

export type RefusalReason =
  /** The wallet named the chain as the thing it could not serve. */
  | "chain"
  /** The wallet named the method as the thing it could not serve. */
  | "method"
  /** The wallet refused the namespace without saying which part failed. */
  | "namespace";

export interface CapabilityRefusal {
  reason: RefusalReason;
  /** Ready to show to the user. */
  message: string;
}

/**
 * WalletConnect v2 SDK error codes, read from @walletconnect/utils SDK_ERRORS
 * (v2.x, as installed) rather than from memory:
 *
 *   5001 USER_REJECTED_CHAINS   5100 UNSUPPORTED_CHAINS
 *   5002 USER_REJECTED_METHODS  5101 UNSUPPORTED_METHODS
 *                               5104 UNSUPPORTED_NAMESPACE_KEY
 *
 * Both the "unsupported" and the "user rejected" variants appear here: wallets
 * differ on which they send for a namespace the bridge simply does not
 * implement, and neither implies a human tapped reject.
 */
const CHAIN_CODES = new Set([5001, 5100]); // USER_REJECTED_CHAINS, UNSUPPORTED_CHAINS
const METHOD_CODES = new Set([5002, 5101]); // USER_REJECTED_METHODS, UNSUPPORTED_METHODS
const NAMESPACE_CODES = new Set([5104]); //    UNSUPPORTED_NAMESPACE_KEY

/**
 * Text fallbacks for wallets that refuse without a usable code. The 1006 /
 * "Proposal Namespaces are invalid" shape is the one LOBSTR has been observed
 * to send; "Non conforming namespaces" is the relay's own wording (internal
 * error 9) when the approval does not satisfy what was required.
 */
const NAMESPACE_TEXT = /non[- ]conforming namespaces|proposal namespaces are invalid|invalid (?:proposal )?namespaces?/i;
const CHAIN_TEXT = /unsupported chains?|chains? (?:are |is )?not supported|unsupported network/i;
const METHOD_TEXT = /unsupported methods?|methods? (?:are |is )?not supported/i;

function codeOf(error: unknown): number | null {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === "number" ? code : null;
}

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" ? message : "";
}

function classify(error: unknown): RefusalReason | null {
  const code = codeOf(error);
  if (code !== null) {
    if (CHAIN_CODES.has(code)) return "chain";
    if (METHOD_CODES.has(code)) return "method";
    if (NAMESPACE_CODES.has(code)) return "namespace";
  }

  // Codes win over text, so a wallet that sends a precise code is never
  // downgraded to the vaguer "namespace" answer by an incidental word.
  const text = messageOf(error);
  if (!text) return null;
  if (CHAIN_TEXT.test(text)) return "chain";
  if (METHOD_TEXT.test(text)) return "method";
  if (NAMESPACE_TEXT.test(text)) return "namespace";
  return null;
}

/**
 * The wallet is picked inside WalletConnect's own modal, so GeoTrust usually
 * does not learn which product it was - the kit reports only "WalletConnect",
 * the transport. Naming a wallet we were never told about would be the same
 * over-claiming this module exists to avoid, hence the anonymous default.
 */
interface WalletLabel {
  /** Capitalised, for the start of a sentence. */
  subject: string;
  /** Lower case, for mid-sentence use. */
  object: string;
}

function explain(reason: RefusalReason, wallet: WalletLabel): string {
  const preamble = `GeoTrust is running on the Stellar test network (${REQUIRED_CHAIN}).`;
  // Said once, because the distinction between a wallet and its WalletConnect
  // bridge is the whole point of this message.
  const scope =
    `This is a limitation of the WalletConnect integration in ${wallet.object}, ` +
    `not of the wallet app itself - it may still support testnet when connected ` +
    `another way - and it is not a failed or cancelled connection.`;

  switch (reason) {
    case "chain":
      return (
        `${preamble} ${wallet.subject} refused the WalletConnect session ` +
        `because its WalletConnect integration does not offer that network. ` +
        `${scope} To use GeoTrust, connect with a wallet whose WalletConnect ` +
        `integration supports ${REQUIRED_CHAIN}.`
      );
    case "method":
      return (
        `${preamble} ${wallet.subject} accepted the network but refused the ` +
        `WalletConnect session because its WalletConnect integration does not ` +
        `offer the signing method GeoTrust requires (${REQUIRED_METHOD}). ${scope}`
      );
    case "namespace":
      return (
        `${preamble} ${wallet.subject} refused the WalletConnect session as ` +
        `incompatible. GeoTrust asks for the ${REQUIRED_CHAIN} network and the ` +
        `${REQUIRED_METHOD} signing method, and the wallet did not say which of ` +
        `the two it cannot provide. ${scope}`
      );
  }
}

function labelFor(wallet?: string | null): WalletLabel {
  const name = wallet?.trim();
  // "WalletConnect" is the transport the kit reports, not a wallet product;
  // using it would produce "the WalletConnect integration in WalletConnect".
  if (!name || name.toLowerCase() === "walletconnect") {
    return { subject: "The wallet you selected", object: "the wallet you selected" };
  }
  return { subject: name, object: name };
}

/**
 * @param error   whatever `authModal()` / `getAddress()` threw
 * @param wallet  display name of the wallet the user picked, when GeoTrust
 *                actually knows it
 * @returns the refusal, or null when this is not a capability mismatch and the
 *          caller should fall back to its normal error handling
 */
export function describeCapabilityRefusal(
  error: unknown,
  wallet?: string | null,
): CapabilityRefusal | null {
  const reason = classify(error);
  if (!reason) return null;
  return { reason, message: explain(reason, labelFor(wallet)) };
}
